<template>
  <div class="p-6 bg-gray-100 dark:bg-gray-900 min-h-screen">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-semibold text-gray-800 dark:text-gray-200">{{ t('applicationsView.title') }}</h1>
      <button class="btn" @click="openCreateModal">
        <Icon icon="tabler:circle-plus" class="me-2"/>
        {{ t('applicationsView.createApplication') }}
      </button>
    </div>

    <div v-if="applications.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 space-x-4">
      <div v-for="app in applications" :key="app.applicationId" 
           class="card card-compact bg-base-100 dark:bg-gray-800 shadow-md hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-1 relative border-t-4 border-primary rounded-lg">
        
        <div class="absolute top-2 right-2 rtl:left-2 rtl:right-auto z-10 flex space-x-1">
          <button class="btn btn-xs btn-ghost btn-circle" @click="openEditModal(app)" title="Edit Settings">
           <Icon icon="tabler:settings" class="text-lg text-gray-500 hover:text-primary" />
          </button>
          <button class="btn btn-xs btn-ghost btn-circle" @click="duplicateApplication(app)" title="Duplicate Application">
           <Icon icon="tabler:copy" class="text-lg text-gray-500 hover:text-primary" />
          </button>
          <button class="btn btn-xs btn-ghost btn-circle" @click="openDeleteConfirmModal(app)" title="Delete Application">
           <Icon icon="tabler:trash" class="text-lg text-gray-500 hover:text-error" />
          </button>
        </div>

        <div class="card-body p-5 flex flex-col">
          <h2 class="card-title text-lg text-gray-900 dark:text-gray-100 truncate mb-1" :title="app.appName">{{ app.appName }}</h2>
          <p class="text-gray-600 dark:text-gray-400 text-sm mb-2 line-clamp-2 h-10">{{ isDefinedAndNotEmpty(app.description) != false ? app.description : t('applicationsView.noDescription') }}</p>
          
          <div class="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
           <Icon icon="tabler:users-group" class="mr-2 flex-shrink-0 text-lg" />
            <span v-if="isDefinedAndNotEmpty(app.divisionnames) != false && app.divisionnames.length > 0" class="truncate" :title="app.divisionnames.join(', ')">
              {{ app.divisionnames.join(', ') }}
            </span>
            <span v-else class="truncate text-gray-400 italic">{{ t('applicationsView.privateToCreator') }}</span>
          </div>

          <div class="text-xs text-gray-500 dark:text-gray-400 space-y-0.5 mb-4">
             <p class="truncate" :title="formatDate(app.createdAt)">{{ t('applicationsView.created', { date: formatDate(app.createdAt) }) }}</p>
             <p class="truncate" :title="formatDate(app.updatedAt)">{{ t('applicationsView.updated', { date: formatDate(app.updatedAt) }) }}</p>
          </div>

          <div class="card-actions justify-start rtl:justify-end mt-auto">
            <button class="btn btn-sm btn-outline" @click="openGeneratedApp(app)">
              <Icon icon="tabler:external-link" class="me-1.5 text-white"/> <p class="text-white">{{ t('applicationsView.openApp') }}</p>
            </button>
            <button class="btn btn-sm btn-outline" @click="navigateToAgent(app)">
               <Icon icon="tabler:pencil" class="me-1.5 text-base"/> {{ t('applicationsView.modifyApp') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-10 bg-white dark:bg-gray-800 rounded-lg shadow">
      <Icon icon="tabler:apps" class="text-6xl text-gray-400 dark:text-gray-500 mb-4" />
      <h3 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">{{ t('applicationsView.emptyStateTitle') }}</h3>
      <p class="text-gray-500 dark:text-gray-400 mb-4">{{ t('applicationsView.emptyStateSubtitle') }}</p>
      <button class="btn" @click="openCreateModal">
        <Icon icon="tabler:circle-plus" class="mr-2"/>
        {{ t('applicationsView.emptyStateButton') }}
      </button>
    </div>

    <dialog id="application_modal" class="modal" :class="{'modal-open': showModal === true}">
      <div class="modal-box w-11/12 max-w-2xl">
        <form method="dialog">
            <button class="btn btn-sm btn-circle btn-ghost absolute top-2" :class="currentLanguage === 'he' ? 'left-2' : 'right-2'" @click="closeApplicationModal">✕</button>
        </form>
        <div class="flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-700 dark:to-indigo-800 p-4 -m-6 mb-4 rounded-t-lg">
            <Icon icon="tabler:wand" class="text-3xl text-white"/>
            <h3 class="text-xl font-bold text-white">
                <span v-if="isCreating === true">{{ t('applicationsView.modal.generateTitle') }}</span>
                <span v-else-if="isEditingSettings === true">{{ t('applicationsView.modal.editTitle') }}</span>
                <span v-else>{{ t('applicationsView.modal.detailsTitle') }}</span>
            </h3>
        </div>

        <p v-if="isCreating === true" class="text-sm text-gray-600 dark:text-gray-400 mb-4 px-1">{{ t('applicationsView.modal.createDescription') }}</p>
        <p v-else-if="isEditingSettings === true" class="text-sm text-gray-600 dark:text-gray-400 mb-4 px-1">{{ t('applicationsView.modal.editDescription') }}</p>

        <div class="space-y-4 py-4">
          <div>
            <label class="label">
              <span class="label-text">{{ t('applicationsView.labels.appName') }}</span>
            </label>
            <input type="text" v-model="currentApp.appName" :placeholder="t('applicationsView.placeholders.appName')" class="input input-bordered w-full" :disabled="isCreating === false && isEditingSettings === false"/>
          </div>

          <div v-if="isCreating === true">
            <label class="label">
              <span class="label-text">{{ t('applicationsView.labels.initialPrompt') }}</span>
            </label>
             <textarea v-model="currentApp.initialPrompt" :placeholder="t('applicationsView.placeholders.initialPrompt')" class="textarea textarea-bordered w-full" :disabled="isCreating === false" rows="3"></textarea>
          </div>

          <div>
            <label class="label">
              <span class="label-text">{{ t('applicationsView.labels.description') }}</span>
            </label>
            <textarea v-model="currentApp.description" :placeholder="t('applicationsView.placeholders.description')" class="textarea textarea-bordered w-full" :disabled="isCreating === false && isEditingSettings === false" rows="2"></textarea>
          </div>

          <div>
            <MultiSelect 
              :label="t('applicationsView.labels.shareWithDivisions')"
              :placeholder="t('applicationsView.placeholders.selectDivisions')"
              v-model="currentApp.divisionIds"
              :options="divisionOptions"
              id-field="value"
              label-field="label"
              :is-loading="divisions.length === 0 && isLoading === false && showModal === true"
              :max-displayed-items="3" 
              :disabled="isCreating === false && isEditingSettings === false"
            />
           </div>

          <div v-if="isCreating === false && isEditingSettings === false && generatedHtmlResponse !== ''" class="mt-4">
                <h4 class="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">{{ t('applicationsView.generatedPreview') }}</h4>
                <iframe
                    :srcdoc="generatedHtmlResponse"
                    class="w-full h-60 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                    frameborder="0"
                    sandbox="allow-scripts allow-same-origin"
                    title="Generated Application Preview">
                </iframe>
           </div>

          <div v-if="isCreating === false && isEditingSettings === false" class="mt-4">
            <h4 class="text-lg font-medium mb-2 text-gray-800 dark:text-gray-200">{{ t('applicationsView.generatedCode') }}</h4>
            <pre class="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm text-gray-700 dark:text-gray-300 overflow-auto max-h-60"><code>{{ currentApp.pageCode || 'No code generated yet.' }}</code></pre>
          </div>
        </div>

        <div class="modal-action">
          <button class="btn mr-2" @click="closeApplicationModal">{{ t('applicationsView.buttons.cancel') }}</button>
          <button v-if="isCreating === true" class="btn" @click="createApplication" :disabled="isLoading === true">
            <span v-if="isLoading === true" class="loading loading-spinner loading-sm mr-2"></span>
            <Icon v-if="isLoading === false" icon="ph:rocket-launch-bold" class="mr-2"/>
            {{ t('applicationsView.buttons.generateApp') }}
          </button>
          <button v-else-if="isEditingSettings === true" class="btn" @click="updateApplicationSettings" :disabled="isLoading === true">
            <span v-if="isLoading === true" class="loading loading-spinner loading-sm mr-2"></span>
           <Icon v-if="isLoading === false" icon="tabler:device-floppy" class="mr-2"/>
            {{ t('applicationsView.buttons.saveChanges') }}
          </button>
        </div>
      </div>
    </dialog>

    <dialog class="modal modal-bottom sm:modal-middle" :class="{'modal-open': showLoadingModal === true}">
      <div class="modal-box">
        <div v-if="modalState.type === 'loading'" class="flex flex-col items-center justify-center">
          <span class="loading loading-spinner loading-lg mb-4 text-primary"></span>
          <h3 class="font-bold text-lg mb-2">{{ modalState.title }}</h3>
          <p class="text-sm text-gray-500 mb-4">{{ t('applicationsView.loadingModal.generatingCodeWait') }}</p>
          <p class="text-sm text-gray-600 dark:text-gray-400">{{ currentTip }}</p>
        </div>

        <div v-else-if="modalState.type === 'error'" class="flex flex-col items-center justify-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-16 w-16 text-error mb-4" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <h3 class="font-bold text-lg mb-2 text-error">{{ modalState.title }}</h3>
          <p class="text-sm text-gray-700 dark:text-gray-300 py-4">{{ modalState.message }}</p>
          <div class="modal-action mt-4 justify-center">
             <button class="btn" @click="closeModal">Close</button>
          </div>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop" @click="closeModalOnErrorState">
         <button>close</button>
       </form>
    </dialog>


    <dialog id="delete_confirm_modal" class="modal" :class="{'modal-open': showDeleteConfirmModal === true}">
      <div class="modal-box">
        <form method="dialog">
            <button class="btn btn-sm btn-circle btn-ghost absolute top-2"
                    :class="currentLanguage === 'he' ? 'left-2' : 'right-2'"
                    @click="closeDeleteConfirmModal">✕</button>
        </form>
        <div class="flex items-center space-x-3 mb-4">
            <Icon icon="tabler:alert-triangle" class="text-3xl text-warning"/>
            <h3 class="text-xl font-bold">{{ t('applicationsView.deleteModal.confirmDeletion') }}</h3>
        </div>
        <p v-if="appToDelete !== null" class="py-4">{{ t('applicationsView.deleteModal.confirmMessage', { appName: appToDelete.appName }) }}</p>
        <p v-else class="py-4">{{ t('applicationsView.deleteModal.confirmMessageGeneric') }}</p>
        <div class="modal-action">
          <button class="btn mr-2" @click="closeDeleteConfirmModal">{{ t('applicationsView.deleteModal.cancel') }}</button>
          <button class="btn btn-error" @click="confirmDeleteApplication" :disabled="isDeleting === true">
            <span v-if="isDeleting === true" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('applicationsView.deleteModal.delete') }}
          </button>
        </div>
      </div>
       <form method="dialog" class="modal-backdrop" @click="closeDeleteConfirmModal">
         <button>close</button>
       </form>
    </dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue/dist/offline';
import MultiSelect from '@/components/MultiSelect.vue';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';


const applications = ref([]);
const divisions = ref([]);
const showModal = ref(false);
const isCreating = ref(false);
const isLoading = ref(false);
const currentApp = ref({
  applicationId: null,
  appName: '',
  initialPrompt: '',
  description: '',
  pageCode: '',
  divisionIds: [],
});
const generatedHtmlResponse = ref('');
const isEditingSettings = ref(false);

const showLoadingModal = ref(false);
const modalState = reactive({
  type: 'loading',
  title: 'Generating Application...',
  message: ''
});
const currentTip = ref('');
const tipInterval = ref(null);

const showDeleteConfirmModal = ref(false);
const appToDelete = ref(null);
const isDeleting = ref(false);

const appGenerationTips = [
  'AI is sketching the user interface...',
  'Generating interactive components...',
  'Writing the underlying application logic...',
  'Optimizing the code for performance...',
  'Connecting the dots... almost there!',
  'Polishing the pixels...',
  'Running final checks on the generated code...',
  'Packaging your application...',
  'Remember: You can refine the app further after this initial generation.',
  'Specify details clearly in your prompt for better results.',
  'Think about user interaction when describing your app.',
];

const divisionOptions = computed(() => {
    return divisions.value.map(div => ({
        label: div.divisionname,
        value: div.divisionid
    }));
});

const toast = useToast();
const router = useRouter();
const { t, currentLanguage } = useI18n();

const fetchApplications = async () => {
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/applications/list`);
    applications.value = response.data;
  } catch (error) {
    console.error('Error fetching applications:', error);
     toast.error('Failed to fetch applications.');
  }
};

const fetchDivisions = async () => {
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/divisions`);
    divisions.value = response.data;
  } catch (error) {
    console.error('Error fetching divisions:', error);
     toast.error('Failed to fetch divisions.');
  }
};

const formatDate = (dateString) => {
  if (dateString === null || dateString === undefined || dateString === '') return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime()) === true) {
        return 'Invalid Date';
    }
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return 'Invalid Date';
  }
};

const resetCurrentApp = () => {
  currentApp.value = {
    applicationId: null,
    appName: '',
    initialPrompt: '',
    description: '',
    pageCode: '',
    divisionIds: [],
  };
  generatedHtmlResponse.value = '';
  isEditingSettings.value = false;
};

const openCreateModal = () => {
  resetCurrentApp();
  isCreating.value = true;
  isEditingSettings.value = false;
  showModal.value = true;
};

const openEditModal = (app) => {
  resetCurrentApp();
  isCreating.value = false;
  isEditingSettings.value = true;
  currentApp.value = {
    applicationId: app.applicationId,
    appName: app.appName,
    initialPrompt: '',
    description: isDefinedAndNotEmpty(app.description) != false ? app.description : '',
    pageCode: '',
    divisionIds: isDefinedAndNotEmpty(app.divisions) != false ? app.divisions.map(d => d.divisionId) : [],
  };
  generatedHtmlResponse.value = '';
  showModal.value = true;
};

const navigateToAgent = (app) => {
    if (app === undefined || app === null) {
        toast.error("Cannot navigate: Invalid app data provided.");
        return;
    }
    if (app.applicationId !== undefined && app.applicationId !== null) {
        router.push({ name: 'ApplicationAgent', params: { appId: app.applicationId } });
    } else {
        toast.error("Could not open application editor: Invalid ID.");
    }
};

const openGeneratedApp = (app) => {
    if (app !== undefined && app !== null && app.applicationId !== undefined && app.applicationId !== null) {
        const safeAppId = encodeURIComponent(String(app.applicationId));
        const url = `/app/${safeAppId}/fullscreen`;
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
    }
    console.error("Cannot open app: Invalid application data provided.", app);
    toast.error("Could not open application: Invalid ID.");
};

function startLoadingTips() {
  let index = 0;
  currentTip.value = appGenerationTips[0];
  if (tipInterval.value !== null) {
      clearInterval(tipInterval.value);
  }
  tipInterval.value = setInterval(() => {
    index = (index + 1) % appGenerationTips.length;
    currentTip.value = appGenerationTips[index];
  }, 5000);
}

function stopLoadingTips() {
  if (tipInterval.value !== null) {
    clearInterval(tipInterval.value);
    tipInterval.value = null;
  }
  currentTip.value = '';
}

function closeModal() {
    showLoadingModal.value = false;
    modalState.type = 'loading';
    modalState.title = 'Generating Application...';
    modalState.message = '';
    stopLoadingTips();
}

function closeModalOnErrorState(event) {
  if (modalState.type === 'error') {
    closeModal();
  } else {
     console.log('Backdrop click ignored during loading.');
  }
}

const createApplication = async () => {
  if (currentApp.value.appName === '' || currentApp.value.appName === null || currentApp.value.initialPrompt === '' || currentApp.value.initialPrompt === null) {
     toast.warning('App Name and Initial Prompt are required.');
    return;
  }
  if (isEditingSettings.value === true) {
    toast.info('In edit mode. Use Save Changes.');
    return;
  }
  isLoading.value = true; 
  generatedHtmlResponse.value = ''; 

 
  showModal.value = false;

  
  modalState.type = 'loading';
  modalState.title = 'Generating Your Application...';
  modalState.message = ''; 
  showLoadingModal.value = true;
  startLoadingTips();

  try {
    const payload = {
      appName: currentApp.value.appName,
      initialPrompt: currentApp.value.initialPrompt,
      description: currentApp.value.description,
      divisionIds: currentApp.value.divisionIds,
    };

    const response = await axios.post(`${globalState.apiEndpoint}/api/applications/create`, payload);

  
    stopLoadingTips();
    showLoadingModal.value = false;
    isLoading.value = false;

    generatedHtmlResponse.value = isDefinedAndNotEmpty(response.data.answer) != false ? response.data.answer : ''; 
    const newAppId = response.data.applicationId;

    if (generatedHtmlResponse.value !== '') {
        toast.success('Application generated successfully! Preview shown below.');
        await fetchApplications(); 
        
        isCreating.value = false; 
        currentApp.value.applicationId = newAppId;
        currentApp.value.pageCode = generatedHtmlResponse.value; 
        const newAppDetails = await axios.get(`${globalState.apiEndpoint}/api/applications/get/${newAppId}`);
        currentApp.value.divisionIds = newAppDetails.data.divisions ? newAppDetails.data.divisions.map(d => d.divisionId) : [];
        showModal.value = true; 

    } else if (newAppId !== undefined && newAppId !== null) {
        toast.success('Application created successfully! Redirecting to editor...');
        await fetchApplications();
        router.push({ name: 'ApplicationAgent', params: { appId: newAppId } });
    } else {
        throw new Error("Application ID or HTML content not returned from server.");
    }

  } catch (error) {
    stopLoadingTips();
    isLoading.value = false; 

   
    modalState.type = 'error';
    modalState.title = 'Generation Failed';
    modalState.message = error.response?.data?.error || 'An unexpected error occurred during generation. Please check your prompt or try again later.';
    showLoadingModal.value = true; 

  } 
};

const updateApplicationSettings = async () => {
  if (currentApp.value.appName === '' || currentApp.value.appName === null) {
     toast.warning('App Name is required.');
    return;
  }
  if (currentApp.value.applicationId === null) {
    toast.error('Application ID is missing. Cannot update.');
    return;
  }

  isLoading.value = true;
  const payload = {
    appName: currentApp.value.appName,
    description: currentApp.value.description,
    divisionIds: currentApp.value.divisionIds,
  };

  try {
    await axios.put(`${globalState.apiEndpoint}/api/applications/${currentApp.value.applicationId}`, payload);
    toast.success(`Application "${currentApp.value.appName}" updated successfully.`);
    await fetchApplications();
    showModal.value = false;
    resetCurrentApp();
  } catch (error) {
    console.error('Error updating application settings:', error);
    toast.error(error.response?.data?.error || 'Failed to update application settings.');
  } finally {
    isLoading.value = false;
  }
};

const openDeleteConfirmModal = (app) => {
  appToDelete.value = app;
  showDeleteConfirmModal.value = true;
};

const closeDeleteConfirmModal = () => {
  showDeleteConfirmModal.value = false;
  appToDelete.value = null;
  isDeleting.value = false;
};

const confirmDeleteApplication = async () => {
  if (appToDelete.value === null || appToDelete.value.applicationId === null) {
    toast.error('No application selected for deletion.');
    closeDeleteConfirmModal();
    return;
  }

  isDeleting.value = true;
  try {
    await axios.delete(`${globalState.apiEndpoint}/api/applications/${appToDelete.value.applicationId}`);
    toast.success(`Application "${appToDelete.value.appName}" deleted successfully.`);
    await fetchApplications();
  } catch (error) {
    console.error('Error deleting application:', error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false || `Failed to delete application "${appToDelete.value.appName}".`);
  } finally {
    closeDeleteConfirmModal();
  }
};

function closeApplicationModal() {
  showModal.value = false;
}

const duplicateApplication = async (app) => {
    if (app === undefined || app === null || app.applicationId === undefined || app.applicationId === null) {
        toast.error('Cannot duplicate: Invalid application data provided.');
        return;
    }

    try {
        const response = await axios.post(`${globalState.apiEndpoint}/api/applications/duplicate/${app.applicationId}`, {});
        
        toast.success(`Application "${response.data.appName}" duplicated successfully.`);
        await fetchApplications();
    } catch (error) {
        console.error('Error duplicating application:', error);
        toast.error(error.response?.data?.error || 'Failed to duplicate application.');
    }
};

onMounted(() => {
  fetchApplications();
  fetchDivisions();
});
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style> 